import { BonusApplication } from '../../services/bonus/types';
import { TimelineEvent } from '../../components/Timeline';
import { getTimelineConfig, TimelineStep } from './timelineConfigs';
import { getTimelineColor } from '../../components/Timeline/TimelineColors';

const getDateForRole = (
  app: BonusApplication,
  role: string,
  status: string
) => {
  switch (status) {
    case "Created":
      return app.createdOn;
    case "Draft":
      return app.createdOn;
    case "Submitted":
      return app.submittedOn;
    case "Bonus_Recipient_Review":
      return app.bonusRecipientReviewedOn;
    case "SPM_Review":
      return app.spmReviewedOn;
    case "DM_Review":
      return app.dmReviewedOn;
    case "DCCM_Review":
      return app.dccmReviewedOn;
    case "VP_Review":
      return app.vpReviewedOn;
    case "Ops_Review":
      return app.opsReviewedOn;
    case "Payment":
    case "Payroll_Processing":
      return app.paymentDate || app.payrollProcessingOn;
    case "Rejected":
      return app.rejectedOn;
    default:
      return null;
  }
};

const getUserFullNameForRole = (
  app: BonusApplication,
  role: string,
  status: string
) => {
  switch (status) {
    case "Created":
      return app.createdByName;
    case "Draft":
      return app.createdByName;
    case "Submitted":
      return app.submittedByName;
    case "Bonus_Recipient_Review":
      return app.bonusRecipientName;
    case "SPM_Review":
      return app.spmReviewedByName;
    case "DM_Review":
      return app.dmReviewedByName;
    case "DCCM_Review":
      return app.dccmReviewedByName;
    case "VP_Review":
      return app.vpReviewedByName;
    case "Ops_Review":
      return app.opsReviewedByName;
    case "Payment":
    case "Payroll_Processing":
      return app.payrollProcessedByName;
    case "Rejected":
      return app.rejectedByName;
    default:
      return null;
  }
};

const isStepCompleted = (
  app: BonusApplication,
  role: string,
  status: string
) => {
  switch (status) {
    case "Created":
      return !!app.createdBy;
    case "Draft":
      return !!app.createdBy;
    case "Submitted":
      return !!app.submittedBy && !!app.submittedOn;
    case "Bonus_Recipient_Review":
      return !!app.bonusRecipientName && !!app.bonusRecipientReviewedOn;
    case "SPM_Review":
      return !!app.spmReviewedBy && !!app.spmReviewedOn;
    case "DM_Review":
      return !!app.dmReviewedBy && !!app.dmReviewedOn;
    case "DCCM_Review":
      return !!app.dccmReviewedBy && !!app.dccmReviewedOn;
    case "VP_Review":
      return !!app.vpReviewedBy && !!app.vpReviewedOn;
    case "Ops_Review":
      return !!app.opsReviewedBy && !!app.opsReviewedOn;
    case "Payment":
      return !!app.payrollProcessedBy && !!app.paymentReference;
    case "Payroll_Processing":
      return !!app.payrollProcessedBy && !!app.paymentReference;
    case "Rejected":
      return !!app.rejectedBy && !!app.rejectedOn;
    default:
      return false;
  }
};

export const getTimelineEvents = (
  applicationType: string,
  app: BonusApplication
): TimelineEvent[] => {
  const year = app.year;
  const config: TimelineStep[] = getTimelineConfig(applicationType, year);

  const currentStatusIndex = config.findIndex(
    (step) => step.status === app.currentStatus
  );

  const isRejected = app.currentStatus === "Rejected";

  return config.map((step, index) => {
    const isActive = step.status === app.currentStatus;
    const isBeforeCurrent = currentStatusIndex > index;
    const completed = isStepCompleted(app, step.role, step.status);

    let color: string;

    if (step.status === "Rejected" && isActive) {
      color = getTimelineColor("Rejected", "active");
    } else if (step.status === "Rejected" && completed) {
      color = getTimelineColor("Rejected", "muted");
    } else if (step.status === "Rejected" && !completed) {
      color = getTimelineColor("Rejected", "pending");
    } else if (isActive) {
      color = getTimelineColor(step.role, "active");
    } else if (isBeforeCurrent && completed) {
      color = getTimelineColor(step.role, "muted");
    } else if (!completed && !isActive) {
      color = getTimelineColor(step.role, "pending");
    } else {
      color = getTimelineColor(step.role, "default");
    }

    return {
      status: step.status,
      role: step.role,
      color,
      completed,
      date: getDateForRole(app, step.role, step.status),
      userFullName: getUserFullNameForRole(app, step.role, step.status),
    };
  });
};

export const formatStatusLabel = (status?: string | null) => {
  return status?.replaceAll("_", " ") || "";
};

export const getRejectionInfo = (app: BonusApplication) => {
  if (app.currentStatus !== "Rejected") {
    return null;
  }

  return {
    rejectedBy: app.rejectedByName || app.rejectedBy,
    rejectedOn: app.rejectedOn,
    rejectionReason: app.rejectionReason,
    rejectedAt: app.rejectedAt,
  };
};

export const canReject = (role: string): boolean => {
  const nonRejectableRoles = ["Payroll", "Paid", "Rejected"];
  return !nonRejectableRoles.includes(role);
};